import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, ChevronRight, Ruler, Layers, Phone } from "lucide-react";

const TrimworkPage = () => {
  return (
    <div>
      <Helmet>
        <title>Skirting Boards & Architraves | Trimwork Fitting | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="Professional skirting board and architrave fitting in Greater Manchester. Neat mitre joints, good preparation, tidy finish. 12-month guarantee. Call 07300 405045." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/trimwork" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What's included in skirting board fitting?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I remove the old skirting, fit the new boards with neat mitres and scribes, fill all nail holes and gaps, and sand ready for painting. You just need to paint it."
                }
              },
              {
                "@type": "Question",
                "name": "How long does it take to fit skirting in a room?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A typical room takes around half a day. A full house of skirting and architraves usually takes 2-3 days depending on the size and complexity."
                }
              },
              {
                "@type": "Question",
                "name": "Can you match my existing skirting profile?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Usually yes. I can source most standard profiles. If it's an unusual one, I'll let you know and we can discuss alternatives."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need to supply the materials?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can supply your own, or I can pick them up for you. I know where to get good quality MDF and hardwood at fair prices."
                }
              },
              {
                "@type": "Question",
                "name": "What about gaps between skirting and the wall?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I caulk along the top edge so you get a clean line where the skirting meets the wall. No unsightly gaps."
                }
              },
              {
                "@type": "Question",
                "name": "Will there be much mess?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work tidily and clean up as I go. There'll be some dust from cutting, but I'll hoover up before I leave."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Interior Trimwork</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Skirting & Architraves
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Skirting boards and architraves fitted with neat mitres and tight joints.
                Proper preparation for a clean, professional finish.
              </p>
              
              <ul className="space-y-4 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  "Tight mitre joints",
                  "Scribed internal corners",
                  "Gaps filled and caulked",
                  "Ready for decorating"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-200 text-base md:text-lg">
                    <div className="w-7 h-7 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-[#C9A227]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-col gap-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg h-14 px-8 w-full" data-testid="trimwork-get-quote-btn">
                    Get a Free Quote
                    <ChevronRight className="ml-2 h-6 w-6" />
                  </Button>
                </Link>
                <a href="tel:07300405045" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg h-14 w-full">
                    <Phone className="mr-2 h-6 w-6" />
                    07300 405045
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_3648a13c-ee29-4e40-9949-2c424ba9ffea/artifacts/bnf93lib_image.png"
                  alt="Skirting board fitting"
                  className="w-full h-[550px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">What I Do</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3A3A3A]">
              Trimwork Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <Card className="group p-6 md:p-8 border-0 shadow-lg bg-white">
              <div className="flex items-start gap-4">
                <div className="p-4 bg-[#2D5A4A] rounded-xl flex-shrink-0">
                  <Ruler className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#3A3A3A] mb-3">Skirting Boards</h3>
                  <p className="text-gray-600 mb-4">
                    Skirting boards fitted with care. I use scribing for internal corners and cut clean mitres for external corners. 
                    All gaps filled and sanded ready for painting.
                  </p>
                  <ul className="space-y-2">
                    {["All profiles & heights", "MDF or hardwood", "Old skirting removed", "Gaps filled & caulked"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="h-4 w-4 text-[#2D5A4A] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="group p-6 md:p-8 border-0 shadow-lg bg-white">
              <div className="flex items-start gap-4">
                <div className="p-4 bg-[#2D5A4A] rounded-xl flex-shrink-0">
                  <Layers className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#3A3A3A] mb-3">Architraves</h3>
                  <p className="text-gray-600 mb-4">
                    Architraves fitted around doors and windows with neat mitre joints. 
                    I take time to get the reveals even and the mitres tight.
                  </p>
                  <ul className="space-y-2">
                    {["Door & window surrounds", "Matching profiles", "Even reveals", "Tight mitre joints"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="h-4 w-4 text-[#2D5A4A] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">My Approach</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A] mb-4">
              Good Preparation, Neat Finish
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { title: "Measure carefully", desc: "I measure twice and cut once. Takes a bit longer but means less waste and better results." },
              { title: "Prepare the walls", desc: "Old skirting removed cleanly. Walls checked and any issues dealt with before fitting starts." },
              { title: "Cut accurate joints", desc: "Mitres cut precisely. Internal corners scribed for a tighter fit that won't gap over time." },
              { title: "Fix securely", desc: "Skirting and architraves fixed properly so they stay put. No relying on filler to hide gaps." },
              { title: "Fill & sand", desc: "All nail holes filled, joints caulked, everything sanded smooth." },
              { title: "Ready for paint", desc: "You get trimwork that's ready for your decorator, or for you to paint yourself." }
            ].map((step, i) => (
              <div key={i} className="bg-white p-5 rounded-xl shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="h-5 w-5 text-[#2D5A4A]" />
                  <h3 className="font-bold text-[#3A3A3A]">{step.title}</h3>
                </div>
                <p className="text-gray-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#3A3A3A] mb-4">
            Supply Your Own or I Can Source
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Happy to fit materials you've bought, or I can pick them up for you. 
            I know where to get good quality skirting and architraves at fair prices.
          </p>
          <Link to="/contact">
            <Button className="bg-[#2D5A4A] hover:bg-[#234839] h-12 px-8">
              Discuss Your Project
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-10 sm:py-14 md:py-20 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Common Questions</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3A3A3A]">
              Trimwork FAQs
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { 
                q: "What's included in skirting board fitting?", 
                a: "I remove the old skirting, fit the new boards with neat mitres and scribes, fill all nail holes and gaps, and sand ready for painting. You just need to paint it." 
              },
              { 
                q: "How long does it take to fit skirting in a room?", 
                a: "A typical room takes around half a day. A full house of skirting and architraves usually takes 2-3 days depending on the size and complexity." 
              },
              { 
                q: "Can you match my existing skirting profile?", 
                a: "Usually yes. I can source most standard profiles. If it's an unusual one, I'll let you know and we can discuss alternatives." 
              },
              { 
                q: "Do I need to supply the materials?", 
                a: "You can supply your own, or I can pick them up for you. I know where to get good quality MDF and hardwood at fair prices." 
              },
              { 
                q: "What about gaps between skirting and the wall?", 
                a: "I caulk along the top edge so you get a clean line where the skirting meets the wall. No unsightly gaps." 
              },
              { 
                q: "Will there be much mess?", 
                a: "I work tidily and clean up as I go. There'll be some dust from cutting, but I'll hoover up before I leave." 
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white p-4 sm:p-5 rounded-xl shadow-sm">
                <h3 className="font-bold text-[#3A3A3A] mb-2 text-sm sm:text-base">{faq.q}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-28 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5">
            Need Skirting or Architraves?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8">
            Get in touch for a free quote. I'll give you a fair price for a neat job.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/contact" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-14 px-10 text-lg w-full">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold h-14 px-10 text-lg w-full">
                <Phone className="mr-2 h-6 w-6" />
                Call 07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrimworkPage;
